import React, { useState } from 'react';
import {
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Container,
  Paper,
  Box,
  CircularProgress,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import Navbar from '../../../../globals/components/Navbar';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiClient } from '../../../../api/httpClient';
import { useSelector } from 'react-redux';
import {
  buildInternalUserPayload,
  INTERNAL_USER_ENDPOINTS,
} from './internalUserFormConfig';
import { useInternalUserEmailCheck } from './useInternalUserEmailCheck';
import {
  InternalUserAccessFlags,
  InternalUserBasicFields,
  InternalUserEmailField,
  InternalUserFeaturePermissions,
  InternalUserPermissionLevelField,
} from './InternalUserFormFields';

const CreateInternalUser = () => {
  const navigate = useNavigate();
  const [permissionLevel, setPermissionLevel] = useState('');
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    email: '',
    lastName: '',
    phoneNumber: '',
  });
  const [isPiiUser, setIsPiiUser] = useState(false);
  const [isCriticalUser, setIsCriticalUser] = useState(false);
  const [sendInvite, setSendInvite] = useState(false);

  const userdetails = useSelector((state) => state.auth.user);
  const isCritical = userdetails?.is_critical_user;

  const {
    emailExists,
    emailChecking,
    emailDialogOpen,
    handleEmailChange,
    handleDialogClose,
    handleDialogContinue,
    handleDialogCancel,
  } = useInternalUserEmailCheck({
    onEmailClear: () => setFormData((prev) => ({ ...prev, email: '' })),
  });

  const handleCancel = () => {
    navigate(-1);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === 'email') {
      handleEmailChange(value);
    }
  };

  const handlePermissionChange = (event) => {
    const { value, checked } = event.target;
    setPermissions((prevPermissions) =>
      checked ? [...prevPermissions, value] : prevPermissions.filter((perm) => perm !== value)
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    const payload = buildInternalUserPayload({
      formData,
      permissionLevel,
      permissions,
      isPiiUser,
      isCriticalUser,
      sendInvite,
    });

    try {
      const response = await apiClient(INTERNAL_USER_ENDPOINTS.create, 'POST', payload);
      if (response) {
        toast.success(
          sendInvite
            ? 'Internal User created and invite sent successfully'
            : 'Internal User created successfully'
        );
      }
      navigate(-1);
    } catch (error) {
      toast.error('Error submitting form');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <Container maxWidth="md" style={{ marginTop: '3rem' }}>
        <Paper elevation={3} style={{ padding: '2rem', backgroundColor: '#f5f5f5', borderRadius: '15px' }}>
          <Typography variant="h4" gutterBottom style={{ textAlign: 'center', fontWeight: 'bold' }}>
            Create New Internal User
          </Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <InternalUserEmailField
                formData={formData}
                onInputChange={handleInputChange}
                emailExists={emailExists}
                emailChecking={emailChecking}
                emailDialogOpen={emailDialogOpen}
                onDialogClose={handleDialogClose}
                onDialogContinue={handleDialogContinue}
                onDialogCancel={handleDialogCancel}
              />
              <InternalUserBasicFields formData={formData} onInputChange={handleInputChange} />
              <InternalUserPermissionLevelField
                permissionLevel={permissionLevel}
                onChange={(e) => setPermissionLevel(e.target.value)}
              />
              <InternalUserAccessFlags
                isCritical={isCritical}
                isPiiUser={isPiiUser}
                isCriticalUser={isCriticalUser}
                onPiiChange={setIsPiiUser}
                onCriticalChange={setIsCriticalUser}
              />
              <InternalUserFeaturePermissions
                permissionLevel={permissionLevel}
                permissions={permissions}
                onPermissionChange={handlePermissionChange}
              />
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sendInvite}
                      onChange={(e) => setSendInvite(e.target.checked)}
                    />
                  }
                  label="Send Invite Email"
                />
              </Grid>
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: 2 }}>
                  <CircularProgress />
                </Box>
              )}
              <Grid size={{ xs: 12 }} style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
                <Button variant="contained" color="warning" onClick={handleCancel} style={{ marginRight: '1rem' }}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="primary" disabled={loading}>
                  Submit
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </>
  );
};

export default CreateInternalUser;
